// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  FiCheckCircle,
  FiRefreshCw,
  FiZap,
  FiDatabase,
  FiPieChart
} from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import Cookies from "js-cookie";

// StatusCard component (unchanged)
const StatusCard = ({ title, status, icon }) => {
  const statusConfig = {
    connected: { color: 'from-green-400 to-emerald-500', text: 'Connected' },
    disconnected: { color: 'from-red-400 to-red-500', text: 'Not Connected' },
    running: { color: 'from-green-400 to-emerald-500', text: 'Running' },
    stopped: { color: 'from-yellow-400 to-yellow-500', text: 'Stopped' },
    healthy: { color: 'from-green-400 to-emerald-500', text: 'Healthy' },
    unhealthy: { color: 'from-red-400 to-red-500', text: 'Unhealthy' },
    checking: { color: 'from-blue-400 to-cyan-500', text: 'Checking...' },
    unknown: { color: 'from-gray-400 to-slate-500', text: 'Unavailable' },
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return (
    <div className="relative h-full bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all">
      <div className="flex flex-col items-center space-y-4">
        <div
          className={`bg-gradient-to-br ${config.color} rounded-full p-3 text-white shadow-lg`}
        >
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <div
          className={`text-sm font-medium ${config.color.replace('from-', 'text-').split(' ')[0]
            }`}
        >
          {config.text}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  // system status state
  const [status, setStatus] = useState({
    kafka: 'checking',
    consumer: 'checking',
    overall: 'checking'
  });

  // analytics data state
  const [analyticsData, setAnalyticsData] = useState({
    delivered_and_read: 0,
    delivered_not_read: 0,
    sent_not_delivered: 0,
    failed: 0,
    total_messages: 0,
    avg_delivery_time_seconds: 0,
  });

  // fetch system health
  const checkSystemStatus = async () => {
    setStatus({ kafka: 'checking', consumer: 'checking', overall: 'checking' });

    try {
      const token = Cookies.get("authToken");

      const res = await fetch('/api/api/health', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setStatus({
        kafka: data.components.kafka_producer === 'connected' ? 'connected' : 'disconnected',
        consumer: data.components.kafka_consumer === 'running' ? 'running' : 'stopped',
        overall: data.status === 'healthy' ? 'healthy' : 'unhealthy'
      });

    } catch (e) {
      console.error('Health check failed', e);
      setStatus({ kafka: 'unknown', consumer: 'unknown', overall: 'unknown' });
    }
  };

  // fetch analytics once on mount
  useEffect(() => {
    checkSystemStatus();

    const fetchAnalytics = async () => {
      try {
        const token = Cookies.get("authToken");

        const res = await fetch('/api/api/analytics/message_summary', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setAnalyticsData(data);
      } catch (e) {
        console.error('Analytics fetch failed', e);
      }
    };

    fetchAnalytics();
  }, []);

  useEffect(() => {
    const token = Cookies.get("authToken");
    if (!token) window.location.href = "/";
  }, []);

  // build chart data from analytics
  const chartData = [
    { name: 'Delivered and Read', value: analyticsData.delivered_and_read, color: '#10B981' },
    { name: 'Delivered and Not Read', value: analyticsData.delivered_not_read, color: '#F59E0B' },
    { name: 'Sent and Not Delivered', value: analyticsData.sent_not_delivered, color: '#3B82F6' },
    { name: 'Failed', value: analyticsData.failed, color: '#EF4444' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mx-auto" style={{ maxWidth: '1800px' }}>

          {/* System Health Monitor */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  System Health Monitor
                </h2>
                <p className="text-gray-600 mt-1">
                  Real-time service status updates
                </p>
              </div>
              <button
                className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all"
                onClick={checkSystemStatus}
              >
                <FiRefreshCw className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700 font-medium">
                  Refresh Status
                </span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <StatusCard
                title="Kafka Cluster"
                status={status.kafka}
                icon={<FiDatabase className="w-6 h-6" />}
              />
              <StatusCard
                title="Event Consumer"
                status={status.consumer}
                icon={<FiZap className="w-6 h-6" />}
              />
              <StatusCard
                title="System Health"
                status={status.overall}
                icon={<FiCheckCircle className="w-6 h-6" />}
              />
            </div>
          </div>

          {/* Message Analytics */}
          <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Message Analytics
                </h2>
                <p className="text-gray-600 mt-1">
                  Last 30 days delivery statistics
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <FiPieChart className="w-6 h-6 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">
                  Real-time updates
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

              {/* Pie Chart */}
              <div className="h-80 sm:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend & Stats */}
              <div className="space-y-6">
                {/* Legend */}
                <div className="space-y-4">
                  {chartData.map(item => (
                    <div
                      key={item.name}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="font-medium text-gray-700">
                          {item.name}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Messages</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {analyticsData.total_messages}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Avg. Delivery Time</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {analyticsData.avg_delivery_time_seconds.toFixed(1)}s
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
